import type {FetchVacanciesQuery} from '@rocket-ui/lk-api';

export type OffersTab = 'all' | 'invited' | 'applied';

export type OffersOrderBy = 'published_at' | 'reward_cents';
export type OffersOrderDirection = 'asc' | 'desc';

export type OffersSort = {
	by: OffersOrderBy;
	direction: OffersOrderDirection;
};

export type OffersExtraFilters = {
	rewardCentsFrom?: number;
	includeHidden?: boolean;
};

export const DEFAULT_OFFERS_SORT: OffersSort = {
	by: 'published_at',
	direction: 'desc',
};

export const OFFERS_TABS: Array<{id: OffersTab; label: string}> = [
	{id: 'all', label: 'Все'},
	{id: 'invited', label: 'Вас пригласили'},
	{id: 'applied', label: 'Вы откликнулись'},
];

export const OFFERS_SORT_OPTIONS: Array<{id: OffersSort; label: string}> = [
	{id: {by: 'published_at', direction: 'desc'}, label: 'Новые предложения'},
	{id: {by: 'reward_cents', direction: 'asc'}, label: 'Оплата по возрастанию'},
	{id: {by: 'reward_cents', direction: 'desc'}, label: 'Оплата по убыванию'},
];

const OFFERS_TABS_SET = new Set<string>(OFFERS_TABS.map((tab) => tab.id));
const OFFERS_ORDER_BY_SET = new Set<string>(['published_at', 'reward_cents']);
const OFFERS_ORDER_DIRECTION_SET = new Set<string>(['asc', 'desc']);

export function isSameOffersSort(a: OffersSort, b: OffersSort): boolean {
	return a.by === b.by && a.direction === b.direction;
}

export function createVacanciesQuery(tab: OffersTab, sort: OffersSort): FetchVacanciesQuery {
	const query: FetchVacanciesQuery = {
		'filters[order][by]': sort.by,
		'filters[order][direction]': sort.direction,
	};

	if (tab === 'invited') query['filters[invited]'] = true;
	if (tab === 'applied') query['filters[applied]'] = true;

	return query;
}

export type OffersUrlState = {
	tab: OffersTab;
	sort: OffersSort;
	extraFilters: OffersExtraFilters;
};

export function parseOffersTab(value: string | null): OffersTab {
	if (value && OFFERS_TABS_SET.has(value)) return value as OffersTab;
	return 'all';
}

export function parseOffersSort(by: string | null, direction: string | null): OffersSort {
	const orderBy = by && OFFERS_ORDER_BY_SET.has(by) ? (by as OffersOrderBy) : DEFAULT_OFFERS_SORT.by;
	const orderDirection =
		direction && OFFERS_ORDER_DIRECTION_SET.has(direction)
			? (direction as OffersOrderDirection)
			: 'desc';

	return {by: orderBy, direction: orderDirection};
}

export function parseOffersSearchParams(searchParams: URLSearchParams): OffersUrlState {
	const rewardFrom = searchParams.get('rewardFrom');
	const rewardCentsFrom =
		rewardFrom != null && rewardFrom !== '' && Number.isFinite(Number(rewardFrom))
			? Math.max(0, Math.round(Number(rewardFrom)))
			: undefined;

	return {
		tab: parseOffersTab(searchParams.get('tab')),
		sort: parseOffersSort(searchParams.get('sort'), searchParams.get('direction')),
		extraFilters: {
			rewardCentsFrom,
			includeHidden: searchParams.get('includeHidden') === '1' ? true : undefined,
		},
	};
}

export function buildOffersSearchParams(state: OffersUrlState): URLSearchParams {
	const params = new URLSearchParams();

	if (state.tab !== 'all') params.set('tab', state.tab);

	if (!isSameOffersSort(state.sort, DEFAULT_OFFERS_SORT)) {
		params.set('sort', state.sort.by);
		if (state.sort.direction !== 'desc') {
			params.set('direction', state.sort.direction);
		}
	}

	if (state.extraFilters.rewardCentsFrom != null) {
		params.set('rewardFrom', String(state.extraFilters.rewardCentsFrom));
	}
	if (state.extraFilters.includeHidden) params.set('includeHidden', '1');

	return params;
}
